import React, { useState, useMemo, FormEvent } from 'react';
import { fetchEventData } from './uscaParser';
import { EventData } from './types';
import {
  computeByTeam,
  computeByWinLoss,
  teamStatsToCsv,
  winLossStatsToCsv,
  TeamStats,
  WinLossStats,
} from './statsCalculator';
import './USCAEventStats.css';

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const USCAEventStats: React.FC = () => {
  const [eventId, setEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'byTeam' | 'byWinLoss'>('byTeam');

  const teamStats = useMemo(() => eventData ? computeByTeam(eventData) : [], [eventData]);
  const winLossStats = useMemo(() => eventData ? computeByWinLoss(eventData) : [], [eventData]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!eventId.trim()) return;

    setLoading(true);
    setError('');
    setEventData(null);

    try {
      const data = await fetchEventData(eventId.trim());
      setEventData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch event data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="usca-event-stats">
          <h1>USCA Event Stats</h1>

          <form className="usca-input-form" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Enter Event ID (e.g. 46)"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              disabled={loading}
            />
            <button type="submit" disabled={loading || !eventId.trim()}>
              {loading ? 'Loading...' : 'Fetch Event'}
            </button>
          </form>

          {loading && (
            <div className="usca-progress">
              <span>Fetching event data...</span>
            </div>
          )}

          {error && <div className="usca-error">{error}</div>}

          {eventData && (
            <>
              <div className="usca-tabs">
                <button
                  className={`usca-tab ${activeTab === 'byTeam' ? 'active' : ''}`}
                  onClick={() => setActiveTab('byTeam')}
                >
                  By Team
                </button>
                <button
                  className={`usca-tab ${activeTab === 'byWinLoss' ? 'active' : ''}`}
                  onClick={() => setActiveTab('byWinLoss')}
                >
                  Winner vs Loser
                </button>
              </div>

              {activeTab === 'byTeam' && (
                <div className="usca-tab-content">
                  <button
                    className="usca-copy-btn"
                    onClick={() => downloadCsv(teamStatsToCsv(teamStats), `event_${eventId}_by_team.csv`)}
                  >
                    Download CSV
                  </button>
                  <div className="usca-table-wrapper">
                    <table className="usca-stats-table">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th>Team</th>
                          <th>Hammer Success</th>
                          <th>Hammer Total</th>
                          <th>Force Success</th>
                          <th>Force Total</th>
                          <th>Steal Success</th>
                          <th>Steal Total</th>
                          <th>Steal Def Success</th>
                          <th>Steal Def Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamStats.map((s: TeamStats) => (
                          <tr key={s.team}>
                            <td>{s.event}</td>
                            <td>{s.team}</td>
                            <td>{s.hammerSuccess}</td>
                            <td>{s.hammerTotal}</td>
                            <td>{s.forceSuccess}</td>
                            <td>{s.forceTotal}</td>
                            <td>{s.stealSuccess}</td>
                            <td>{s.stealTotal}</td>
                            <td>{s.stealDefenseSuccess}</td>
                            <td>{s.stealDefenseTotal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'byWinLoss' && (
                <div className="usca-tab-content">
                  <button
                    className="usca-copy-btn"
                    onClick={() => downloadCsv(winLossStatsToCsv(winLossStats), `event_${eventId}_win_loss.csv`)}
                  >
                    Download CSV
                  </button>
                  <div className="usca-table-wrapper">
                    <table className="usca-stats-table">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th>Winner/Loser</th>
                          <th>Hammer Success</th>
                          <th>Hammer Total</th>
                          <th>Force Success</th>
                          <th>Force Total</th>
                          <th>Steal Success</th>
                          <th>Steal Total</th>
                          <th>Steal Def Success</th>
                          <th>Steal Def Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {winLossStats.map((s: WinLossStats) => (
                          <tr key={s.label}>
                            <td>{s.event}</td>
                            <td>{s.label}</td>
                            <td>{s.hammerSuccess}</td>
                            <td>{s.hammerTotal}</td>
                            <td>{s.forceSuccess}</td>
                            <td>{s.forceTotal}</td>
                            <td>{s.stealSuccess}</td>
                            <td>{s.stealTotal}</td>
                            <td>{s.stealDefenseSuccess}</td>
                            <td>{s.stealDefenseTotal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="usca-results">
                <details>
                  <summary>
                    Parsed {eventData.games.length} games from event {eventData.eventId} — Click to expand JSON
                  </summary>
                  <button
                    className="usca-copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(eventData, null, 2));
                    }}
                  >
                    Copy JSON
                  </button>
                  <pre>{JSON.stringify(eventData, null, 2)}</pre>
                </details>
              </div>
            </>
          )}
        </div>
      </header>
    </div>
  );
};

export default USCAEventStats;
